import * as React from "react";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterCombobox } from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import AbortJobModal from "../Jobs/AbortJobModal";
import JobRequestStatusChip from "../Jobs/JobRequestStatusChip";
import { compareBy, Order, stableSort } from "../../helpers/TableHelpers";
import JobsRepository from "../../repositories/JobsRepository";
import {ExportTemplateFilesToCsvRequest, JobRequest,} from "../../models/Jobs/JobRequest";
import {JobRequestStatus, JobRequestStatusColors, JobRequestStatusIcons, JobRequestStatusLabels,} from "../../models/Jobs/JobRequestStatus";
import {UserRole} from "../../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import {QUERY_CONFIG} from "../../constants/QueryConfig";
import {cn} from "@/lib/utils";
import {
  drawerFiltersBoxClassName,
  drawerFilterItemClassName,
  drawerTableWrapperClassName,
} from "../common/Table/TableStyles";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {Button} from "@/components/ui/button";
import {Label} from "@/components/ui/label";
import {Trash2, Download, XCircle} from "lucide-react";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface ExportTemplateFilesToCsvStatusTableProps {
  userRole: UserRole;
  isAssignedUser: boolean;
  organizationId: string;
  templateId?: string;
}

const ExportTemplateFilesToCsvStatusTable: React.FC<ExportTemplateFilesToCsvStatusTableProps> = ({ userRole, isAssignedUser, organizationId, templateId }) => {
  const requiresReason = useRequiresReason();
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const [order, setOrder] = React.useState<Order>("desc");
  const [sort, setSort] = React.useState<keyof ExportTemplateFilesToCsvRequest>("exportName");
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [exportTemplateFilesStatus, setExportTemplateFilesStatus] = useState<ExportTemplateFilesToCsvRequest[] | null>(null);

  const exportsQuery = useQuery({ queryKey: [QUERY_KEYS.EXPORT_TEMPLATE_FILES, templateId, organizationId], queryFn: async () => await jobsRepository.getTemplateFilesCsvGenerationStatus(organizationId, templateId), staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME, refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL, refetchIntervalInBackground: true, refetchOnMount: true, });

  useEffect(() => { if (exportsQuery.data) setExportTemplateFilesStatus(exportsQuery.data); }, [exportsQuery]);

  const handleDownload = (url: string | undefined) => { if (url) window.open(url, "_blank"); };
  const changeSort = (newSort: keyof ExportTemplateFilesToCsvRequest) => setSort(newSort);
  const changeOrder = (newOrder: Order) => setOrder(newOrder);

  const filteredJobs = (exportTemplateFilesStatus || []).filter((job) => statusFilter == null || job.status === statusFilter);
  const sortedJobs = sort ? stableSort<ExportTemplateFilesToCsvRequest>(filteredJobs, compareBy(order, sort)) : filteredJobs;
  const loading = exportsQuery.isFetching || exportsQuery.isError;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={drawerFiltersBoxClassName}>
        <div className={drawerFilterItemClassName}>
          <Label className="text-sm">Status</Label>
          <FilterCombobox
            value={statusFilter}
            onChange={(newValue) => setStatusFilter(newValue)}
            placeholder="Status"
            options={Object.values(JobRequestStatus).filter((status) => status !== JobRequestStatus.New).map((status) => ({
              value: status,
              label: JobRequestStatusLabels[status as JobRequestStatus],
              color: JobRequestStatusColors[status as JobRequestStatus],
              icon: JobRequestStatusIcons[status as JobRequestStatus],
            }))}
          />
        </div>
      </div>
      <div className={cn(drawerTableWrapperClassName, loading && "opacity-50 pointer-events-none")}>
        <Table className="table-fixed" containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[15%] min-w-[48px] text-center" />
              <TableHead className="w-[40%]"><SortableTableColumnHeader label={"Name"} sortProperty={"exportName"} getSort={() => sort} setSort={(newSort) => changeSort(newSort as keyof ExportTemplateFilesToCsvRequest)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead className="w-[30%]"><SortableTableColumnHeader label={"Status"} sortProperty={"status"} getSort={() => sort} setSort={(newSort) => changeSort(newSort as keyof JobRequest)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead className="w-[15%]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedJobs.map((exportCsvJob) => {
              const canEdit = exportCsvJob.status === JobRequestStatus.Pending || exportCsvJob.status === JobRequestStatus.Completed;
              return (
                <TableRow key={exportCsvJob.id}>
                  <TableCell>
                    {requiresReason ? (
                      <ActionConfirmationModal actionConfig={ACTION_CONFIGS.EXPORT_TEMPLATES_LIBRARY_FILES_VIEW} onConfirm={async (reason) => { try { await jobsRepository.auditLogTemplateFilesViewCsvExportDownload(exportCsvJob.id, reason); handleDownload(exportCsvJob.exportedCsvUrl); } catch (error) { throw error; } }}>
                        <Button variant="ghost" size="icon-sm" className="text-primary" aria-label="Download CSV" disabled={!exportCsvJob.exportedCsvUrl}><Download className="size-4" /></Button>
                      </ActionConfirmationModal>
                    ) : (
                      <Button variant="ghost" size="icon-sm" className="text-primary" aria-label="Download CSV" onClick={() => handleDownload(exportCsvJob.exportedCsvUrl)} disabled={!exportCsvJob.exportedCsvUrl}><Download className="size-4" /></Button>
                    )}
                  </TableCell>
                  <TableCell><span className="text-sm truncate block">{exportCsvJob.exportName}</span></TableCell>
                  <TableCell><JobRequestStatusChip status={exportCsvJob.status} /></TableCell>
                  <TableCell>
                    {canEdit && (
                      <Button variant="ghost" size="icon-sm" className="text-destructive" aria-label="Cancel" onClick={() => setAbortJob(exportCsvJob)}>
                        {exportCsvJob.status === JobRequestStatus.Completed ? (<Trash2 className="size-4" />) : (<XCircle className="size-4" />)}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
              {sortedJobs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={99} className="h-24 text-center text-muted-foreground">
                    No exports.
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </div>
      {abortJob && (
        <AbortJobModal open={abortJob !== null} jobRequest={abortJob} onClose={() => setAbortJob(null)}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_TEMPLATE_FILES, templateId] });
            setAbortJob(null);
          }}
        />
      )}
    </div>
  );
};

export default ExportTemplateFilesToCsvStatusTable;
